import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtINR, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Printer, Pencil, Trash2, Trash, PlusCircle, Building2, Palette } from "lucide-react";
import { SearchBox, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { inDateRange } from "@/components/crm/Filters";
import ItemAutocomplete from "@/components/crm/ItemAutocomplete";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import WhatsAppShareButton from "@/components/crm/WhatsAppShareButton";

const THEMES = [
  { value: "tally", label: "Tally Classic", description: "Traditional B&W bordered layout" },
  { value: "modern", label: "Modern Amber", description: "Clean, colored header with soft rows" },
  { value: "corporate", label: "Corporate Elegant", description: "Slate & gold, executive tone" },
];

const emptyItem = { description: "", hsn: "8541", qty: 1, unit: "Nos", rate: 0 };
const empty = {
  customer_id: "", invoice_no: "", invoice_date: new Date().toISOString().slice(0,10),
  place_of_supply: "Rajasthan (08)",
  firm_id: "", theme: "tally",
  items: [{ ...emptyItem }],
  cgst_percent: 6, sgst_percent: 6, igst_percent: 0, round_off: 0, notes: "",
};

export default function TaxInvoices() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [firms, setFirms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = async () => {
    setItems((await api.get("/tax-invoices")).data);
    setCustomers((await api.get("/customers")).data);
    try { setFirms((await api.get("/firms")).data); } catch { setFirms([]); }
    try {
      const { data: co } = await api.get("/settings/company");
      if (co?.default_invoice_theme) {
        setForm(f => (f.theme === "tally" && !f.customer_id ? { ...f, theme: co.default_invoice_theme } : f));
      }
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const num = (v) => parseFloat(v) || 0;
  const taxable = form.items.reduce((s, it) => s + num(it.qty) * num(it.rate), 0);
  const cgst = taxable * num(form.cgst_percent) / 100;
  const sgst = taxable * num(form.sgst_percent) / 100;
  const igst = taxable * num(form.igst_percent) / 100;
  const total = taxable + cgst + sgst + igst + num(form.round_off);

  const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const setItem = (i, k, v) => {
    const arr = [...form.items];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, items: arr });
  };

  const save = async () => {
    try {
      const payload = { ...form, invoice_no: (form.invoice_no || "").trim(), firm_id: form.firm_id || null };
      if (editId) await api.patch(`/tax-invoices/${editId}`, payload);
      else await api.post("/tax-invoices", payload);
      toast.success(editId ? "Invoice updated" : "Invoice created");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (t) => {
    setForm({ ...empty, ...t, invoice_no: t.invoice_no, firm_id: t.firm_id || "", theme: t.theme || "tally", items: (t.items || []).map(i => ({ ...emptyItem, ...i })) });
    setEditId(t.id); setOpen(true);
  };

  const del = async (t) => {
    try {
      await api.delete(`/tax-invoices/${t.id}`);
      toast.success(`Invoice ${t.invoice_no} deleted`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const visible = items
    .filter((t) => matchesQuery(t, search))
    .filter((t) => inDateRange(t.created_at, fFrom, fTo));

  return (
    <div className="space-y-5" data-testid="tax-invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Tax Invoices <span className="text-slate-400 text-lg font-medium">/ टैक्स इनवॉइस</span></h1>
          <p className="text-slate-500 text-sm">Multi-firm billing · 3 themes · Editable serial numbers.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search invoice #, customer..." testid="tax-invoice-search-input" />
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(empty); setEditId(null);}}}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-tax-invoice-btn"><Plus className="w-4 h-4 mr-1"/>New Invoice</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId ? "Edit Tax Invoice" : "New Tax Invoice"}</DialogTitle></DialogHeader>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Building2 className="w-3 h-3"/>Bill from Firm*</Label>
                  <Select value={form.firm_id||""} onValueChange={v=>setForm({...form, firm_id:v})}>
                    <SelectTrigger data-testid="tax-invoice-firm-select"><SelectValue placeholder="Select firm"/></SelectTrigger>
                    <SelectContent>
                      {firms.length === 0 && <div className="p-2 text-xs text-slate-500">Add firms in Settings → Billing Firms first.</div>}
                      {firms.map(f=><SelectItem key={f.id} value={f.id}>{f.name}{f.is_default?" · Default":""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Palette className="w-3 h-3"/>Invoice Theme</Label>
                  <Select value={form.theme} onValueChange={v=>setForm({...form, theme:v})}>
                    <SelectTrigger data-testid="tax-invoice-theme-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {THEMES.map(t=>(
                        <SelectItem key={t.value} value={t.value}>
                          <div><div className="font-medium">{t.label}</div><div className="text-[10px] text-slate-500">{t.description}</div></div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Field label="Invoice No. (editable)">
                  <Input value={form.invoice_no||""} placeholder="Auto (INV-00001)" onChange={e=>setForm({...form, invoice_no:e.target.value})} data-testid="tax-invoice-no-input"/>
                </Field>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Customer*</Label>
                  <Select value={form.customer_id} onValueChange={v=>setForm({...form, customer_id:v})}>
                    <SelectTrigger data-testid="tax-invoice-customer-select"><SelectValue placeholder="Select customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Field label="Invoice Date">
                  <Input type="date" value={form.invoice_date||""} onChange={e=>setForm({...form, invoice_date:e.target.value})}/>
                </Field>
                <Field label="Place of Supply">
                  <Input value={form.place_of_supply||""} onChange={e=>setForm({...form, place_of_supply:e.target.value})}/>
                </Field>
              </div>

              <div className="mt-2">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 flex items-center justify-between">
                  <span>Line Items</span>
                  <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="tax-invoice-add-item"><PlusCircle className="w-3.5 h-3.5 mr-1"/>Add Row</Button>
                </div>
                <div className="mt-2 space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 border border-slate-200 rounded-lg p-2">
                      <div className="col-span-12 sm:col-span-5"><Label className="text-[10px] uppercase text-slate-500">Description</Label>
                        <ItemAutocomplete
                          value={it.description}
                          onChange={(n)=>setItem(i,"description",n)}
                          onPick={(picked)=>{
                            const arr=[...form.items];
                            arr[i]={...arr[i], description:picked.name,
                              unit: picked.unit||arr[i].unit,
                              rate: picked.last_rate||arr[i].rate,
                              hsn: picked.hsn||arr[i].hsn};
                            setForm({...form, items:arr});
                          }}
                          placeholder="Item / product description"
                          testid={`tax-item-desc-${i}`}
                        /></div>
                      <div className="col-span-4 sm:col-span-2"><Label className="text-[10px] uppercase text-slate-500">HSN/SAC</Label>
                        <Input value={it.hsn||""} onChange={e=>setItem(i,"hsn",e.target.value)}/></div>
                      <div className="col-span-3 sm:col-span-1"><Label className="text-[10px] uppercase text-slate-500">Qty</Label>
                        <Input type="number" step="0.01" value={it.qty} onChange={e=>setItem(i,"qty",num(e.target.value))}/></div>
                      <div className="col-span-2 sm:col-span-1"><Label className="text-[10px] uppercase text-slate-500">Unit</Label>
                        <Input value={it.unit||""} onChange={e=>setItem(i,"unit",e.target.value)}/></div>
                      <div className="col-span-3 sm:col-span-2"><Label className="text-[10px] uppercase text-slate-500">Rate</Label>
                        <Input type="number" value={it.rate} onChange={e=>setItem(i,"rate",num(e.target.value))} data-testid={`tax-item-rate-${i}`}/></div>
                      <div className="col-span-12 sm:col-span-1 flex justify-end">
                        {form.items.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" onClick={()=>rmItem(i)} className="text-rose-600 hover:bg-rose-50"><Trash className="w-4 h-4"/></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-2">
                <Field label="CGST %"><Input type="number" step="0.1" value={form.cgst_percent} onChange={e=>setForm({...form, cgst_percent:num(e.target.value)})}/></Field>
                <Field label="SGST %"><Input type="number" step="0.1" value={form.sgst_percent} onChange={e=>setForm({...form, sgst_percent:num(e.target.value)})}/></Field>
                <Field label="IGST % (inter-state)"><Input type="number" step="0.1" value={form.igst_percent} onChange={e=>setForm({...form, igst_percent:num(e.target.value)})}/></Field>
                <Field label="Round Off (±)"><Input type="number" step="0.01" value={form.round_off} onChange={e=>setForm({...form, round_off:num(e.target.value)})}/></Field>
              </div>

              <Field label="Notes"><Textarea rows={2} value={form.notes||""} onChange={e=>setForm({...form, notes:e.target.value})}/></Field>

              <div className="bg-slate-900 text-white rounded-lg p-4 mt-2">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  <div><div className="text-[10px] uppercase text-slate-400">Taxable</div><div className="font-mono font-bold">{fmtINR(taxable)}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-400">CGST</div><div className="font-mono">{fmtINR(cgst)}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-400">SGST</div><div className="font-mono">{fmtINR(sgst)}</div></div>
                  <div><div className="text-[10px] uppercase text-slate-400">IGST</div><div className="font-mono">{fmtINR(igst)}</div></div>
                  <div><div className="text-[10px] uppercase text-amber-300">Grand Total</div><div className="font-mono font-extrabold text-amber-300 text-lg">{fmtINR(total)}</div></div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.customer_id || form.items.length === 0} data-testid="save-tax-invoice-btn">{editId ? "Update Invoice" : "Create Invoice"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo} onClear={()=>{setFFrom("");setFTo("");setSearch("");}} />

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Invoice #</TableHead><TableHead>Firm</TableHead><TableHead>Customer</TableHead>
            <TableHead>Date</TableHead><TableHead>Theme</TableHead>
            <TableHead>Grand Total</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search ? `No invoices matching "${search}"` : "No tax invoices yet."}</TableCell></TableRow>}
            {visible.map(t=>(
              <TableRow key={t.id} data-testid={`tax-invoice-row-${t.invoice_no}`}>
                <TableCell className="font-mono text-xs font-semibold">{t.invoice_no}</TableCell>
                <TableCell className="text-xs">{t.firm_snapshot?.name || "-"}</TableCell>
                <TableCell className="font-medium">{t.customer_name}<div className="text-xs text-slate-500">{t.customer_mobile}</div></TableCell>
                <TableCell className="text-xs">{fmtDate(t.invoice_date || t.created_at)}</TableCell>
                <TableCell><span className="text-[10px] uppercase font-semibold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">{t.theme||"tally"}</span></TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-900">{fmtINR(t.grand_total)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Link to={`/tax-invoices/${t.id}/print`}>
                      <Button size="sm" variant="outline" data-testid={`print-tax-invoice-${t.invoice_no}`}><Printer className="w-3 h-3 mr-1"/>Print</Button>
                    </Link>
                    <WhatsAppShareButton
                      mobile={t.customer_mobile}
                      message={`Namaste ${t.customer_name || ""},\n\nYeh raha aapka Tax Invoice — ${t.invoice_no}.\n\nTaxable: ${fmtINR(t.taxable_amount)}\nGST: ${fmtINR((t.cgst_amount||0)+(t.sgst_amount||0)+(t.igst_amount||0))}\nGrand Total: ${fmtINR(t.grand_total)}\n\nAmount in words: ${t.amount_in_words || ""}\n\n— ${t.firm_snapshot?.name || "MRM Renewables"}`}
                      label="WhatsApp"
                      testid={`whatsapp-tax-invoice-${t.invoice_no}`}
                    />
                    <Button size="sm" variant="ghost" onClick={()=>edit(t)} data-testid={`edit-tax-invoice-${t.invoice_no}`}><Pencil className="w-3.5 h-3.5"/></Button>
                    {user?.role === "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-tax-invoice-${t.invoice_no}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Tax Invoice {t.invoice_no}?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently delete this tax invoice for {t.customer_name}. This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="cancel-delete-tax-invoice">Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={()=>del(t)} data-testid="confirm-delete-tax-invoice">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

function Field({label, children}){
  return <div><Label className="text-xs font-semibold text-slate-600">{label}</Label><div className="mt-1">{children}</div></div>;
}
